import type { NextUnlock } from '../../features/wallet/api';

export type { NextUnlock };

export type BalanceCardInput = {
  total: number;
  withdrawable: number;
  bonusBalance: number;
  /** null = no active audit; undefined = backend predates the field. */
  nextUnlock: NextUnlock | null | undefined;
};

export type BalanceCardView =
  | { state: 'empty' }
  | { state: 'all-unlocked'; total: number }
  | {
      state: 'locked' | 'partial';
      total: number;
      withdrawable: number;
      locked: number;
      /** null when there is no bonus balance — the card then never mentions bonus. */
      bonus: number | null;
      /**
       * pct is the honest number; arcPct is what to draw (3% floor so 0% is still a dot).
       * null when we know money is locked but NOT how far along the rollover is —
       * no milestone from the backend, or a milestone that requires nothing. The
       * card then shows the Available / Locked split and no progress claim.
       */
      ring: { pct: number; arcPct: number } | null;
      /** null exactly when `ring` is null. */
      wagerRemaining: number | null;
      /** null when every active source is exhausted (backend reports 0), or when `ring` is null. */
      unlockAmount: number | null;
    };

const MIN_ARC = 3;

/**
 * Everything the balance card shows, from the wallet numbers and the backend's
 * next-unlock milestone. Kept free of React and i18n so the four states can be
 * checked with plain node tests.
 */
export function balanceCardView(input: BalanceCardInput): BalanceCardView {
  const total = Math.max(0, num(input.total));
  if (total <= 0) return { state: 'empty' };

  const withdrawable = Math.max(0, num(input.withdrawable));
  const next = input.nextUnlock;
  // "All available to withdraw" is a promise, so it needs both halves of the
  // evidence: no active milestone AND a withdrawable amount that covers the
  // headline total. A missing milestone on its own proves nothing — the field
  // is also absent while the audit query is in flight, on a player bundle
  // deployed ahead of the backend, and when a pending withdrawal reservation
  // or an ineligible lot leaves nothing withdrawable with no audit running.
  if (!next && withdrawable >= total) return { state: 'all-unlocked', total };

  const locked = Math.max(0, total - withdrawable);
  const bonusBalance = num(input.bonusBalance);
  const bonus = bonusBalance > 0 ? bonusBalance : null;
  const state = withdrawable > 0 ? 'partial' : 'locked';
  const required = next ? Math.max(0, num(next.wagerRequired)) : 0;
  // No milestone, or a milestone that asks for nothing: we can still state the
  // Available / Locked split honestly, but we cannot draw progress against a
  // requirement we do not have. A zero requirement used to render a full ring
  // over a locked balance, which read as "done" while the money stayed locked.
  if (!next || required <= 0) {
    return { state, total, withdrawable, locked, bonus, ring: null, wagerRemaining: null, unlockAmount: null };
  }

  const remaining = Math.min(required, Math.max(0, num(next.wagerRemaining)));
  const pct = Math.round(((required - remaining) / required) * 100);
  const unlockAmount = num(next.unlockAmount);

  return {
    state,
    total,
    withdrawable,
    locked,
    bonus,
    ring: { pct, arcPct: Math.max(MIN_ARC, pct) },
    wagerRemaining: remaining,
    unlockAmount: unlockAmount > 0 ? unlockAmount : null,
  };
}

function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}
